import { CalendarOutlined, FileDoneOutlined } from "@ant-design/icons";
import { useQuery } from "@tanstack/react-query";
import { Card, Typography } from "antd";
import React, { useEffect } from "react";
import LoaderTsx from "../../../Loader.tsx";
import { currencyFormat } from "../../../../../utils/media";
import { getPaidMetrics, getUnPaidMetrics } from "../ducks/service";
import ProgressWithDetail from "./components/ProgressWithDetail";
import { useDashboardFilterContext } from "../../hooks/DashboardFilterProvider";

const { Title, Text } = Typography;

interface PaidBookingsCardProps {
  title: string;
  subTitle: string;
  score: number;
}

const PaidBookingsCard: React.FC<PaidBookingsCardProps> = React.memo(
  ({ title, subTitle, score }) => {
    const filterData = useDashboardFilterContext();
    const {
      data: paidMetrics,
      isLoading: isLoadingPaid,
      refetch: paidRefresh,
    } = useQuery({
      queryKey: ["paid-metrics", filterData?.queryParams],
      queryFn: () => getPaidMetrics(filterData?.queryParams),
      staleTime: 1000 * 60 * 5, // ✅ 5 minutes
      refetchOnWindowFocus: false, // 👈 disable auto refetch when window regains focus
    });

    const {
      data: unPaidMetrics,
      isLoading: isLoadingUnPaid,
      refetch: unpaidRefresh,
    } = useQuery({
      queryKey: ["unpaid-metrics", filterData?.queryParams],
      queryFn: () => getUnPaidMetrics(filterData?.queryParams),
      staleTime: 1000 * 60 * 5, // ✅ 5 minutes
      refetchOnWindowFocus: false, // 👈 disable auto refetch when window regains focus
    });

    const progressData = [
      {
        label: "About to Expire",
        percentage:
          title === "PAID"
            ? paidMetrics?.result?.aboutToExpireCount
            : unPaidMetrics?.result?.aboutToExpireCount,
        leftValue: "820",
        rightValue:
          title === "PAID"
            ? ` ${currencyFormat(paidMetrics?.result?.aboutToExpireValue)}`
            : `${currencyFormat(unPaidMetrics?.result?.aboutToExpireValue)}`,
      },
      {
        label: "Expired",
        percentage:
          title === "PAID"
            ? paidMetrics?.result?.paidInProgressCount
            : unPaidMetrics?.result?.unpaidInProgressCount,
        leftValue: "500",
        rightValue:
          title === "PAID"
            ? `${currencyFormat(paidMetrics?.result?.paidInProgressValue)}`
            : `${currencyFormat(unPaidMetrics?.result?.unpaidInProgressValue)}`,
      },
    ];

  return (
    <Card
      style={{
        backgroundColor: "#F8F8F8",
        borderRadius: 20,
        border: "none",
        boxShadow: "none",
      }}
    >
      {isLoadingPaid || isLoadingUnPaid ? (
        <LoaderTsx height={"30vh"} />
      ) : (
        <>
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <Title
              level={5}
              style={{
                margin: 0,
                color: "#1F5782",
                textTransform: "uppercase",
                fontWeight: 400,
              }}
            >
              <span style={{ fontWeight: 700 }}>{title}</span> <span style={{ fontWeight: 400 }}>{subTitle}</span>
            </Title>

            <div
              style={{
                width: 48,
                height: 48,
                borderRadius: "50%",
                backgroundColor: "#FFFFFF",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {title === "PAID" ? (
                <FileDoneOutlined style={{ fontSize: 24, color: "#1F5782" }} />
              ) : (
                <CalendarOutlined style={{ fontSize: 24, color: "#1F5782" }} />
              )}
            </div>
          </div>

          <div
            style={{
              margin: "8px 0",
              textAlign: "left",
            }}
          >
            <Title
              level={2}
              style={{
                margin: 0,
                fontSize: 40,
                color: "#234D64",
              }}
            >
              {title === "PAID" ? paidMetrics?.result?.paidBookingsCount : unPaidMetrics?.result?.unpaidBookingsCount}
            </Title>
          </div>

          {progressData &&
            progressData.map((data, index) => (
              <ProgressWithDetail
                key={JSON.stringify(data)}
                label={data.label}
                percentage={data.percentage}
                leftValue={data.leftValue}
                rightValue={data.rightValue}
              />
            ))}
        </>
      )}
    </Card>
  );
});

export default PaidBookingsCard;
